// The Effects panel (issue 1437; design/16 §6): every running effect in one list, grouped by creature, with its side
// (good or bad), the time left, its numbers and a remove button; and one form that adds an effect to several creatures at
// once (Bless on the whole party). The turn tracker lists only the creatures now. Durations count down and the stones'
// dots show as before: the effects are the same `Combatant.effects`, sent one `encounter.effects` act per creature.
//
// Who: the DM, and a co-DM whose powers cover `encounter.effects`, edit. Anyone else reads what they were already sent —
// a player's own encounter as projected — so nothing new reaches a player. Effects held by a token outside the turn order
// (`Encounter.standing`, the server's alone) are listed for the DM, read-only: the server puts them on and takes them up.
import { useEffect, useRef, useState } from 'react';
import {
  EFFECT_MOD_LIMITS, ENCOUNTER_LIMITS, effectBadge, modTargetWords,
  type Combatant, type Effect, type Encounter, type PlayerEncounter, type TableObject,
} from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useNumberWords } from '../../table/numberWords';
import { BTN } from '../ui';
import { EFFECT_ICON_CHOICES } from '../../board/statusIcons';

const num = 'w-12 rounded bg-stone-800 px-1 py-0.5 text-xs';

/**
 * A combatant's effects as last sent and not yet seen back (qa-runs#165, moved here from the tracker). An effects change
 * sends the whole list, so a change made while an earlier one to the same creature was on its way builds on that one,
 * which the list also shows, until the table's own list has caught up; a refusal drops it.
 */
const sentEffects = new Map<string, { effects: Effect[]; acked: boolean }>();
const sameIds = (a: Effect[], b: Effect[]) => a.length === b.length && a.every((e, i) => e.id === b[i]!.id);
/** For tests: nothing left over from another. */
export function clearSentEffects(): void { sentEffects.clear(); }

type Row = { id: string; name: string; effects: Effect[]; combatant: boolean };

function EffectChip({ e, owner, onRemove }: { e: Effect; owner: string; onRemove?: () => void }) {
  const words = useNumberWords();
  return (
    <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] ${e.good ? 'bg-green-900 text-green-100' : 'bg-red-900 text-red-100'}`} data-testid="effect-badge">
      {effectBadge(e)}{e.mods?.length ? <span className="opacity-80" data-testid="effect-mods">{e.mods.map((m) => ` ${m.value > 0 ? '+' : ''}${m.value} ${m.target === 'ac' ? words.defence : modTargetWords(m.target, m.which)}${m.only ? ` (${m.only})` : ''}`).join(',')}</span> : null}
      {onRemove && <button type="button" className="opacity-70 hover:opacity-100" aria-label={`Remove ${e.label} from ${owner}`} onClick={onRemove}>×</button>}
    </span>
  );
}

function AddEffect({ combatants, send }: { combatants: Combatant[]; send: (c: Combatant, add: Effect) => void }) {
  const { systemRules } = useTableState();
  const words = useNumberWords();
  // The system's units of time and bonus types (issue 1303): the shortest unit is the round the fight ticks by.
  const units = systemRules.units;
  const types = ['untyped', ...systemRules.bonusTypes.filter((t) => t !== 'untyped')];
  const [label, setLabel] = useState('');
  const [rounds, setRounds] = useState('');
  const [unitKey, setUnitKey] = useState(units[0]?.key ?? '');
  const unit = units.find((u) => u.key === unitKey) ?? units[0] ?? null;
  const amountLabel = unit ? `${unit.plural[0]!.toUpperCase()}${unit.plural.slice(1)}` : '';
  const [good, setGood] = useState(true);
  // The picture its token badge shows (qa-runs#1440): none is its colour and first letter.
  const [icon, setIcon] = useState('');
  const [modValue, setModValue] = useState('');
  const [modTarget, setModTarget] = useState<'attack' | 'ac'>('attack');
  const [modType, setModType] = useState('untyped');
  const [on, setOn] = useState<ReadonlySet<string>>(new Set());
  const picked = combatants.filter((c) => on.has(c.id));
  if (!combatants.length) return <p className="text-xs text-stone-500" data-testid="effects-none-to-add">Effects go on the turn order's creatures: add some to the turn tracker first.</p>;
  return (
    <form className="flex flex-col gap-1 border-t border-stone-800 pt-2" data-testid="effects-add" onSubmit={(e) => {
      e.preventDefault();
      if (!label.trim() || !picked.length) return;
      const n = !unit || rounds.trim() === '' ? null : Math.max(1, Math.floor(Number(rounds) || 0)) || null;
      // The server stamps the unit and the round from the system's own as it commits (issue 1303).
      const duration = n !== null && unit ? { amount: n, unit, round: units[0]! } : null;
      const v = Math.trunc(Number(modValue));
      const mods = modValue.trim() !== '' && Number.isFinite(v) && v !== 0 ? [{ target: modTarget, value: Math.max(-EFFECT_MOD_LIMITS.value, Math.min(EFFECT_MOD_LIMITS.value, v)), ...(modType !== 'untyped' ? { type: modType } : {}) }] : [];
      // One act per creature, each effect its own id: removing Bless from one leaves the others'.
      for (const c of picked) send(c, { id: crypto.randomUUID(), label: label.trim().slice(0, ENCOUNTER_LIMITS.label), duration, good, ...(icon ? { icon } : {}), ...(mods.length ? { mods } : {}) });
      setLabel(''); setRounds(''); setModValue('');
    }}>
      <fieldset className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs" aria-label="Creatures the effect goes on">
        {combatants.map((c) => (
          <label key={c.id} className="flex items-center gap-0.5">
            <input type="checkbox" aria-label={`Put the effect on ${c.name}`} checked={on.has(c.id)}
              onChange={(e) => { const next = new Set(on); if (e.target.checked) next.add(c.id); else next.delete(c.id); setOn(next); }} />{c.name}
          </label>
        ))}
        <button type="button" className="text-stone-400 underline" onClick={() => setOn(on.size === combatants.length ? new Set() : new Set(combatants.map((c) => c.id)))}>{on.size === combatants.length ? 'none' : 'all'}</button>
      </fieldset>
      <div className="flex flex-wrap items-center gap-1">
        <input aria-label="Effect" className="w-24 rounded bg-stone-800 px-1 py-0.5 text-xs" placeholder="Bless" value={label} maxLength={ENCOUNTER_LIMITS.label} onChange={(e) => setLabel(e.target.value)} />
        {unit && <input aria-label={`${amountLabel} for the effect`} type="number" min={1} className={num} placeholder="∞" title={`${amountLabel}; empty means indefinite — it never ticks`} value={rounds} onChange={(e) => setRounds(e.target.value)} />}
        {units.length > 1 && (
          <select aria-label="Unit for the effect" className="rounded bg-stone-800 px-1 py-0.5 text-[0.6875rem]" value={unit?.key ?? ''} onChange={(e) => setUnitKey(e.target.value)}>
            {units.map((u) => <option key={u.key} value={u.key}>{u.plural}</option>)}
          </select>
        )}
        <label className="flex items-center gap-0.5 text-[0.6875rem]"><input type="checkbox" aria-label="Beneficial effect" checked={good} onChange={(e) => setGood(e.target.checked)} /> good</label>
        <select aria-label="Picture for the effect" className="rounded bg-stone-800 px-1 py-0.5 text-[0.6875rem]" value={icon} onChange={(e) => setIcon(e.target.value)} title="The picture on its tokens; none shows its first letter.">
          <option value="">no picture</option>
          {EFFECT_ICON_CHOICES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input aria-label="Number for the effect" type="number" className={num} placeholder="±" title="Optional: a bonus or penalty attacks fold in while it lasts, such as +1 for Bless." value={modValue} onChange={(e) => setModValue(e.target.value)} />
        <select aria-label="What the effect changes" className="rounded bg-stone-800 px-1 py-0.5 text-[0.6875rem]" value={modTarget} onChange={(e) => setModTarget(e.target.value as 'attack' | 'ac')}>
          <option value="attack">to attack</option>
          <option value="ac">to {words.defence}</option>
        </select>
        <select aria-label="Bonus type of the effect" className="rounded bg-stone-800 px-1 py-0.5 text-[0.6875rem]" value={modType} onChange={(e) => setModType(e.target.value)} title="Bonuses of one type do not stack unless the game system says that type adds up: only the strongest counts.">
          {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <button type="submit" className={BTN} disabled={!label.trim() || !picked.length} aria-label="Add the effect">Add</button>
      </div>
    </form>
  );
}

export function EffectsPanel() {
  const { act, store } = useTableState();
  const mayAct = useMayAct();
  const edits = mayAct('encounter.effects');
  const raw = store.state.encounter as Encounter | PlayerEncounter | undefined;
  const objects = store.state.objects as Record<string, TableObject>;
  const [, redraw] = useState(0);
  const combatants = (raw?.combatants ?? []) as Combatant[];
  const latest = useRef(combatants);
  latest.current = combatants;
  // The table's list moved after the table took ours: it has caught up (or moved on), and is the list again.
  useEffect(() => {
    let moved = false;
    for (const [id, mark] of sentEffects) if (mark.acked) { sentEffects.delete(id); moved = true; }
    if (moved) redraw((x) => x + 1);
  }, [raw]);
  const effectsOf = (c: Combatant): Effect[] => sentEffects.get(c.id)?.effects ?? c.effects ?? [];
  const set = (c: Combatant, effects: Effect[]) => {
    const mark = { effects, acked: false };
    sentEffects.set(c.id, mark);
    redraw((x) => x + 1);
    act({ kind: 'encounter.effects', id: c.id, effects }, (reply) => {
      if (sentEffects.get(c.id) !== mark) return; // a later change is on its way, built on this one
      const now = latest.current.find((x) => x.id === c.id)?.effects ?? [];
      if (!reply.ok || sameIds(now, effects)) { sentEffects.delete(c.id); redraw((x) => x + 1); } else mark.acked = true;
    });
  };
  const rows: Row[] = combatants.map((c) => ({ id: c.id, name: c.name, effects: effectsOf(c), combatant: true }));
  // Effects on tokens outside the turn order (the DM's alone: a player's encounter carries no `standing`).
  for (const [token, effects] of Object.entries((raw as Encounter | undefined)?.standing ?? {})) {
    if (effects.length) rows.push({ id: token, name: objects[token]?.name ?? 'A token', effects, combatant: false });
  }
  const shown = rows.filter((r) => r.effects.length);
  return (
    <div className="flex flex-col gap-2 p-2 text-sm" data-testid="effects-panel">
      {!shown.length && <p className="text-xs text-stone-500" data-testid="effects-empty">No effects are running.</p>}
      <ul className="flex flex-col gap-1.5">
        {shown.map((r) => {
          const c = r.combatant ? combatants.find((x) => x.id === r.id) : undefined;
          return (
            <li key={r.id} data-testid={`effects-${r.id}`}>
              <div className="text-xs text-stone-300">{r.name}{!r.combatant && <span className="text-stone-500" title="On a token outside the turn order; it joins the fight with it."> · not in the turn order</span>}</div>
              <div className="flex flex-wrap gap-1">
                {r.effects.map((e) => <EffectChip key={e.id} e={e} owner={r.name} onRemove={edits && c && effectsOf(c).length ? () => set(c, effectsOf(c).filter((x) => x.id !== e.id)) : undefined} />)}
              </div>
            </li>
          );
        })}
      </ul>
      {edits && <AddEffect combatants={combatants} send={(c, add) => { const now = effectsOf(c); if (now.length < ENCOUNTER_LIMITS.effects) set(c, [...now, add]); }} />}
    </div>
  );
}
